'use client'

import { useMemo } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { isError } from 'lodash'
import { useForm } from 'react-hook-form'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import {
  RangeInputs,
  useRangeFieldErrors,
} from '~/app/accounting/filters/RangeInputs'
import {
  COLUMN_IDS,
  DEFAULT_VALUE_FILTER,
  Direction,
  DirectionAndValueFilterValue,
  directionFromFilter,
  directionToFilter,
  getColumnFilterValue,
  isValueFilterEmpty,
  parseDirectionAndValue,
  setColumnFilterValue,
  usdRangeLabel,
} from '~/app/accounting/filters/helpers'
import { RadioGroupInput } from '~/components/component-library'

type RangeFormValues = { min: string; max: string; direction: Direction }

const DEFAULT_RANGE_FORM: RangeFormValues = {
  min: '',
  max: '',
  direction: directionFromFilter(DEFAULT_VALUE_FILTER),
}

export const ValueFilterButton = ({
  columnFilters,
  setColumnFilters,
}: {
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) => {
  const rawValue = getColumnFilterValue(columnFilters, COLUMN_IDS.USD_VALUE)
  const appliedFilter = useMemo(() => {
    const parsed = parseDirectionAndValue(rawValue)
    return isError(parsed) ? DEFAULT_VALUE_FILTER : parsed
  }, [rawValue])
  const appliedDirection = directionFromFilter(appliedFilter)

  const form = useForm<RangeFormValues>({
    values: {
      min: appliedFilter.min,
      max: appliedFilter.max,
      direction: appliedDirection,
    },
  })
  const { min, max, direction } = form.watch()

  const localFilter: DirectionAndValueFilterValue = {
    min,
    max,
    direction: directionToFilter(direction),
  }

  const isLocalEmpty = isValueFilterEmpty(localFilter)
  const isAppliedEmpty = !rawValue || isValueFilterEmpty(appliedFilter)

  const { minError, maxError } = useRangeFieldErrors({
    min,
    max,
    setError: form.setError,
    clearErrors: form.clearErrors,
  })

  const hasChanged =
    min !== appliedFilter.min ||
    max !== appliedFilter.max ||
    direction !== appliedDirection

  const clearFilter = () => {
    setColumnFilters((prev) =>
      setColumnFilterValue(prev, COLUMN_IDS.USD_VALUE, ''),
    )
  }

  const applyFilter = () => {
    setColumnFilters((prev) =>
      setColumnFilterValue(
        prev,
        COLUMN_IDS.USD_VALUE,
        JSON.stringify(localFilter),
      ),
    )
  }

  const resetToDefault = () => {
    form.reset(DEFAULT_RANGE_FORM)
    clearFilter()
  }

  const activeLabel = useMemo(() => {
    const dir = directionFromFilter(appliedFilter)
    const dirName = dir === 'in' ? 'Inflows' : dir === 'out' ? 'Outflows' : ''

    const valueStr = usdRangeLabel(appliedFilter)

    if (dirName && valueStr) return `${dirName}: ${valueStr}`
    return dirName || valueStr
  }, [appliedFilter])

  return (
    <FilterPopoverButton
      label="Value"
      activeLabel={activeLabel}
      isAppliedEmpty={isAppliedEmpty}
      isLocalEmpty={isLocalEmpty}
      isInvalid={!!minError || !!maxError}
      hasChanged={hasChanged}
      onApply={applyFilter}
      onReset={resetToDefault}
      onClear={resetToDefault}
      onClose={() => {
        if (!hasChanged) return
        form.reset({
          min: appliedFilter.min,
          max: appliedFilter.max,
          direction: appliedDirection,
        })
      }}
    >
      {(focusAction) => (
        <>
          <RadioGroupInput
            control={form.control}
            inputName="direction"
            label="Direction"
            size="small"
            options={[
              { value: 'any', label: 'Any' },
              { value: 'in', label: 'Inflows' },
              { value: 'out', label: 'Outflows' },
            ]}
          />
          <RangeInputs
            control={form.control}
            minName="min"
            maxName="max"
            placeholder="$0"
            prefix="$"
            decimalScale={2}
            onComplete={focusAction}
          />
        </>
      )}
    </FilterPopoverButton>
  )
}
